import { z } from 'zod';
import { passwordSchema, roleSchema } from './session.js';

/**
 * 成员状态。
 *
 * - `active` 已经自己登录过
 * - `pending_first_login` 拿到一次性初始密码，还没首次登录
 * - `pending_invite` 收到邀请邮件，还没点开设密码
 */
export const memberStatusSchema = z.enum(['active', 'pending_first_login', 'pending_invite']);
export type MemberStatus = z.infer<typeof memberStatusSchema>;

export const memberSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: roleSchema,
  status: memberStatusSchema,
  createdAt: z.string(),
});

export type Member = z.infer<typeof memberSchema>;

export const memberListSchema = z.object({
  members: z.array(memberSchema),
});

export const createMemberSchema = z.object({
  name: z.string().trim().min(1, '请填写姓名').max(60),
  email: z.string().trim().toLowerCase().email('请填写可用的邮箱地址'),
  role: roleSchema.default('member'),
});

export type CreateMember = z.infer<typeof createMemberSchema>;

/**
 * 添加成员的结果，两条分支：
 *
 * - `invited` 发信通道可用，系统直接把邀请邮件发出去了
 * - `password_issued` 通道没配，返回一次性初始密码，由管理员线下转交
 *
 * 初始密码只在这个响应里出现一次，库里存的是哈希，之后任何接口都拿不到。
 */
export const createMemberResultSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('invited'),
    member: memberSchema,
  }),
  z.object({
    outcome: z.literal('password_issued'),
    member: memberSchema,
    initialPassword: z.string(),
  }),
]);

export type CreateMemberResult = z.infer<typeof createMemberResultSchema>;

/**
 * 管理员重置某个成员的密码：新密码由管理员自己填，不发邮件、不由系统生成。
 *
 * 这条路不依赖任何消息通道，所以没有通道也能用——它是目前唯一的找回途径。
 */
export const resetMemberPasswordSchema = z.object({
  password: passwordSchema,
});
